export const PRICE_RANGE_STEP = 1;

export type PriceRangeField = 'min' | 'max';

export type PriceRangeInputBounds = {
    min: number;
    max: number;
    step: number;
    appliedMin: number;
    appliedMax: number;
};

export type PriceRangeInputResult = {status: 'reset'} | {status: 'valid'; value: number} | {status: 'invalid'; error: string};

const WHOLE_DOLLAR_PATTERN = /^-?\d+$/;

export function priceRangeInputError(min: number, max: number): string {
    return `Enter a valid price range (${min}-${max}).`;
}

function isOnStep(value: number, min: number, step: number): boolean {
    if (!(step > 0)) {
        return false;
    }

    const offset = (value - min) / step;
    return Number.isInteger(offset);
}

/**
 * Blank/zero reset first, then whole-dollar and range checks.
 * Callers must not write the filter when the result is invalid.
 */
export function parsePriceRangeInput(raw: string, field: PriceRangeField, bounds: PriceRangeInputBounds): PriceRangeInputResult {
    const {min, max, step, appliedMin, appliedMax} = bounds;
    const trimmed = raw.trim();

    if (trimmed === '') {
        return {status: 'reset'};
    }

    const error = priceRangeInputError(min, max);

    if (!WHOLE_DOLLAR_PATTERN.test(trimmed)) {
        return {status: 'invalid', error};
    }

    const value = Number(trimmed);

    if (!Number.isFinite(value) || !Number.isInteger(value)) {
        return {status: 'invalid', error};
    }

    if (value === 0) {
        return {status: 'reset'};
    }

    if (value < min || value > max || !isOnStep(value, min, step)) {
        return {status: 'invalid', error};
    }

    if (field === 'min' && value > appliedMax) {
        return {status: 'invalid', error};
    }

    if (field === 'max' && value < appliedMin) {
        return {status: 'invalid', error};
    }

    return {status: 'valid', value};
}

/**
 * Other endpoint for reversed checks: the value in that field if it is a valid
 * amount, otherwise the last applied store value. Avoids treating max 25 as
 * valid when the min box shows 30 but the store has not caught up yet.
 */
export function otherFieldPriceEnd(raw: string, otherField: PriceRangeField, bounds: PriceRangeInputBounds): number {
    const parsed = parsePriceRangeInput(raw, otherField, {
        ...bounds,
        appliedMin: bounds.min,
        appliedMax: bounds.max,
    });

    if (parsed.status === 'valid') {
        return parsed.value;
    }

    if (parsed.status === 'reset') {
        return otherField === 'min' ? bounds.min : bounds.max;
    }

    return otherField === 'min' ? bounds.appliedMin : bounds.appliedMax;
}
